import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ExtraErrorData } from '@sentry/integrations';
import { history } from './history';

import createApolloClient from './apollo';
import createStores from './stores';
import Weblink from './App';
import config from './config';
import { setContentLocaleChangeHandler } from './stores/contentLocaleStore';
import { STORES } from './constants';
import queries from './queries';
import { beforeSentrySend, ignoredSentryErrors } from './utils/sentry';

if (config.sentryDSN) {
  Sentry.init({
    dsn: config.sentryDSN,
    environment: config.sentryEnvironment,
    release: config.sentryRelease,
    integrations: [new ExtraErrorData({ depth: 3 })],
    normalizeDepth: 4,
    // Don't capture transaction data
    tracesSampleRate: 0.0,
    ignoreErrors: ignoredSentryErrors,
    beforeSend: beforeSentrySend,
  });
}

if (config.isDev) {
  // eslint-disable-next-line
  import('./index.css');
}

const { graphQLURI: SFAPIURL, portalAuthURI } = config;
const portalAuthDomain = window.location.hostname;

const apolloClient = createApolloClient({
  SFAPIURL,
  portalAuthURL: portalAuthURI,
  portalAuthDomain,
});
// prime the cache with store details
apolloClient.query({ query: queries.store.getStoreDetails });

const stores = createStores(false, { history, apolloClient });

let root = null;

const renderWeblink = (generation) => {
  if (root) root.unmount();
  root = createRoot(document.getElementById('weblink-root'));
  root.render(
    <Weblink
      key={generation}
      stores={stores}
      history={history}
      apolloClient={apolloClient}
      weblinkApiConfiguration={{
        uri: SFAPIURL,
        portalAuthDomain,
      }}
    />,
  );
};

// Bumped on every Locale change and used as <Weblink>'s React key, so a
// rebuild throws the old tree away instead of re-rendering it in place with
// the UI state - open dropdowns, scroll position, expanded panels - it had.
// Translated content is wiped on the stores separately; this is just the
// visual reset.
let renderGeneration = 0;
renderWeblink(renderGeneration);

setContentLocaleChangeHandler(() => {
  renderGeneration += 1;
  // The store reset rejects queries still in flight when it lands, so the
  // rebuild - which fires the Locales and store-details queries - waits for
  // it, or those queries come back with "Store reset while query was in
  // flight" and blank the page.
  stores[STORES.STORE_ROOT].wipeContentLocale().then(() => {
    renderWeblink(renderGeneration);
  });
});
